import { useCallback, useEffect, useState } from 'react';
import { notificationsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

export default function useStudentNotifications({ enabled = true, pollMs = 60000 } = {}) {
  const [inbox, setInbox] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadInbox = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await notificationsAPI.getInbox({ limit: 20 });
      const data = res.data?.data;
      setInbox(data?.items || []);
      setUnreadCount(data?.unreadCount || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load notifications'));
    }
  }, [enabled]);

  const loadHistory = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const [inboxRes, historyRes] = await Promise.all([
        notificationsAPI.getInbox({ limit: 30 }),
        notificationsAPI.getMyHistory({ limit: 50 }),
      ]);
      setInbox(inboxRes.data?.data?.items || []);
      setUnreadCount(inboxRes.data?.data?.unreadCount || 0);
      setHistory(historyRes.data?.data?.items || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load notification history'));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    loadInbox();
    if (!pollMs) return undefined;
    const id = setInterval(loadInbox, pollMs);
    return () => clearInterval(id);
  }, [enabled, loadInbox, pollMs]);

  const markRead = async (ids) => {
    await notificationsAPI.markInboxRead({ ids });
    await loadInbox();
  };

  const markAllRead = async () => {
    await notificationsAPI.markInboxRead({ all: true });
    await loadInbox();
  };

  return {
    inbox,
    unreadCount,
    history,
    loading,
    error,
    loadInbox,
    loadHistory,
    markRead,
    markAllRead,
  };
}
